<template>
  <div
    ref="refSwiper"
    class="accordion-gallery-mobile swiper swiper-initialized swiper-horizontal swiper-android"
  >
    <div class="swiper-wrapper">
      <template v-for="(slide, index) in slides">
        <div
          class="swiper-slide"
          style="margin-right: 16px;"
        >
          <div class="accordion-gallery-mobile-card">
            <div class="accordion-gallery-mobile-card__image">
              <nuxt-img
                :src="String(slide.imag_id || slide.image_id || slide.imageId)"
                :placeholder="true"
                width="860"
                loading="lazy"
                :alt="`image-slide-${ index }-${slide.imag_id || slide.image_id}`"
              />
            </div>
            <div class="accordion-gallery-mobile-card__body">
              <div
                class="accordion-gallery-mobile-card__title h5"
                v-html="slide.title"
              />
              <div
                v-if="(slide.link || slide.url)"
                class="accordion-gallery-mobile-card__button"
              >
                <nuxt-link
                  :to="(slide.link || slide.url)"
                  class="btn btn-primary"
                >
                  Подробнее
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div
      ref="refSwiperPagination"
      class="swiper-pagination swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-horizontal"
    />
  </div>
</template>

<script setup lang="ts">
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { useIntersectionObserver } from '@vueuse/core';
import { ref, nextTick } from 'vue';

interface ISlide {
	url?: string | undefined;
	link?: string | undefined;
	title: string | undefined;
  imageId?: string | undefined;
  image_id?: string | undefined;
	link_text: string | undefined;
	imag_id: number;
}

interface IProps {
	slides: ISlide[]
}

const props = withDefaults(defineProps<IProps>(), {
  slides: () => []
});

const refSwiperJs: Ref<any> = ref(null);

const refSwiper: any = ref(null);
const refSwiperPagination: any = ref(null);
const initSwiper = async () => {
  if (!refSwiper.value || !refSwiperPagination.value) {
    console.warn('Swiper refs not ready');
    return;
  }

  // Проверяем, что у нас есть слайды
  if (!props.slides || props.slides.length === 0) {
    console.warn('No slides provided');
    return;
  }

  try {
    const swiperObj = new Swiper(refSwiper.value, {
      modules: [Pagination],
      pagination: {
        el: refSwiperPagination.value,
        enabled: true,
        type: 'bullets',
        clickable: true
      },
      spaceBetween: 16,
      slidesPerView: 1.1,
      lazyPreloadPrevNext: 1,
      breakpoints: {
        640: {
          slidesPerView: 2.2
        }
      }
    });
    refSwiperJs.value = swiperObj;
    console.log('Swiper initialized successfully');
  } catch (error) {
    console.error('Error initializing Swiper:', error);
  }
};

const { stop } = useIntersectionObserver(refSwiper, async ([{ isIntersecting }]) => {
  if (refSwiperJs.value) {
    if (isIntersecting) {
      refSwiperJs.value!.enable();
    } else {
      refSwiperJs.value!.disable();
    }
  }
});
const isUsedZoom: Ref<boolean> = ref(false);
const checkUsedZoom = () => {
  if (!refSwiperJs.value) {
    return;
  }

  const currentScale = window.visualViewport?.scale || 1;
  if (!isUsedZoom.value && currentScale <= 1.1) {
    return;
  }

  if (currentScale <= 1.1) {
    isUsedZoom.value = false;
    refSwiperJs.value!.enable();
    return;
  }

  isUsedZoom.value = true;
  refSwiperJs.value!.disable();
};

// Следим за изменениями в slides
watch(() => props.slides, async (newSlides) => {
  if (newSlides && newSlides.length > 0 && !refSwiperJs.value) {
    await nextTick();
    await initSwiper();
  }
}, { immediate: false });

onMounted(async () => {
  await nextTick();
  await initSwiper();
  if (window && window?.addEventListener) {
    window.addEventListener("touchmove", checkUsedZoom)
  }
});
onBeforeUnmount(() => {
  stop();
  if (refSwiperJs.value) {
    refSwiperJs.value.destroy(true, true);
    refSwiperJs.value = null;
  }
  if (window && window?.removeEventListener) {
    window.removeEventListener("touchmove", checkUsedZoom)
  }
});
</script>

<style lang="scss">
.accordion-gallery-mobile {
	margin: 0 -20px;
	padding: 0 20px;

  .swiper-slide {
    margin-right: 16px;
  }
  .swiper-wrapper {
    min-height: 396px;
  }
	.swiper-pagination {
    gap: 4px;
    display: flex;
		position: relative;
    justify-content: center;
		margin-top: 34px;
    flex-wrap: nowrap;

		& > * {
			opacity: 1;
			width: 10px !important;
			height: 10px !important;
			background-color: #EBEBEB;
      margin: 0!important;

			&.swiper-pagination-bullet-active {
				background-color: black;
			}
		}
	}
}

.accordion-gallery-mobile-card {
	border-radius: 16px;
	border: 1px solid #EBEBEB;
	position: relative;
	overflow: hidden;
  min-height: 376px;
}

.accordion-gallery-mobile-card__image {
	border-radius: 0 0 16px 16px;
	position: relative;
	overflow: hidden;

	img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&:after {
		content: "";
		float: left;
		padding-top: 62.5%;
	}
}

.accordion-gallery-mobile-card__body {
	padding: 24px;
	box-sizing: border-box;
}

.accordion-gallery-mobile-card__title {
	margin-bottom: 24px;
	color: black;
}

.accordion-gallery-mobile-card__button {
	display: flex;

	.btn {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 48px;
		width: 100%;
		font-weight: 600;
	}
}

@media (max-width: 639px) {
  .accordion-gallery-mobile .swiper-slide {
    max-width: calc(100% - 36px);
  }
}
</style>
